import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { SquarePen, X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { useMediaQuery } from '@/shared/components/hooks'
import { useAuthStore } from '@/shared/state/authstore'
import {
  CHAT_COPY,
  chatKeys,
  useChatBadge,
  useChatCapabilities,
  useChatUiStore,
  useConversations,
  type ChatTriage,
} from '@/entities/chat'
import { StreamPill } from './StreamPill'
import { ConversationList } from './ConversationList'
import { ChatThread } from './ChatThread'
import { NewChatButton } from './NewChatButton'
import { BroadcastList } from './BroadcastList'
import { BroadcastPane } from './BroadcastPane'
import {
  clampDockSize,
  DOCK_DEFAULT_HEIGHT,
  DOCK_DEFAULT_WIDTH,
  dockStorageKey,
  persistDockPatch,
  readPersistedDock,
  useChatDockControls,
  type DockSize,
} from './useChatDockControls'
import { useNextUnread } from './useNextUnread'
import { useChatShortcuts } from './useChatShortcuts'

const MOBILE_QUERY = '(max-width: 639px)'

const TRIAGE_CHIPS: { value: ChatTriage; label: string }[] = [
  { value: 'unread', label: 'Unread' },
  { value: 'all', label: 'All' },
  { value: 'broadcasts', label: 'Broadcasts' },
]

export function ChatDock() {
  const queryClient = useQueryClient()
  const routerState = useRouterState()
  const isChatRoute = routerState.location.pathname.startsWith('/chat')
  const { enabled: permitted, canOps, canBroadcast } = useChatCapabilities()

  const accessEnded = useChatUiStore((state) => state.accessEnded)
  const streamStatus = useChatUiStore((state) => state.streamStatus)
  const triage = useChatUiStore((state) => state.triage)
  const setTriage = useChatUiStore((state) => state.setTriage)
  const setDockOpenRaw = useChatUiStore((state) => state.setDockOpen)
  const setSelectedConversationRaw = useChatUiStore((state) => state.setSelectedConversation)

  const userId = useAuthStore((state) => state.userId)
  const accountId = useAuthStore((state) => state.accountId)
  const scopeKey = dockStorageKey(userId, accountId)

  const { dockOpen, selectedConversationId, setDockOpen, toggleDock, selectConversation } = useChatDockControls()

  const [size, setSize] = useState<DockSize>({ width: DOCK_DEFAULT_WIDTH, height: DOCK_DEFAULT_HEIGHT })
  const hydratedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (hydratedKeyRef.current === scopeKey) return
    hydratedKeyRef.current = scopeKey
    const persisted = readPersistedDock(scopeKey)
    setDockOpenRaw(persisted.open)
    setSelectedConversationRaw(persisted.selectedId)
    setSize({ width: persisted.width, height: persisted.height })
  }, [scopeKey, setDockOpenRaw, setSelectedConversationRaw])

  const isMobile = useMediaQuery(MOBILE_QUERY)

  useEffect(() => {
    const clampToViewport = () => setSize((current) => clampDockSize(current))
    window.addEventListener('resize', clampToViewport)
    return () => window.removeEventListener('resize', clampToViewport)
  }, [])

  const resizeOriginRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null)

  const handleResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      resizeOriginRef.current = { x: event.clientX, y: event.clientY, width: size.width, height: size.height }
    },
    [size.width, size.height],
  )

  const sizeFromPointer = (event: ReactPointerEvent<HTMLDivElement>, origin: NonNullable<typeof resizeOriginRef.current>) =>
    clampDockSize({
      width: origin.width + (origin.x - event.clientX),
      height: origin.height + (origin.y - event.clientY),
    })

  const handleResizePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = resizeOriginRef.current
    if (!origin) return
    setSize(sizeFromPointer(event, origin))
  }, [])

  const handleResizePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const origin = resizeOriginRef.current
      if (!origin) return
      resizeOriginRef.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
      const final = sizeFromPointer(event, origin)
      setSize(final)
      persistDockPatch(scopeKey, final)
    },
    [scopeKey],
  )

  const selectedBroadcastId = useChatUiStore((state) => state.selectedBroadcastId)
  const composingBroadcast = useChatUiStore((state) => state.composingBroadcast)
  const selectBroadcast = useChatUiStore((state) => state.selectBroadcast)
  const composeBroadcast = useChatUiStore((state) => state.composeBroadcast)
  const isBroadcastMode = triage === 'broadcasts' && selectedConversationId == null
  const broadcastPaneOpen = isBroadcastMode && (composingBroadcast || selectedBroadcastId != null)

  const conversationsQuery = useConversations()
  const conversations = conversationsQuery.data ?? []
  const unreadBadge = useChatBadge()
  const visibleConversations = triage === 'unread' ? conversations.filter((row) => row.hasUnread) : conversations
  const { nextUnreadId, goNext } = useNextUnread(conversations, selectedConversationId)
  const chips = canBroadcast ? TRIAGE_CHIPS : TRIAGE_CHIPS.filter((chip) => chip.value !== 'broadcasts')

  useEffect(() => {
    if (!canBroadcast && useChatUiStore.getState().triage === 'broadcasts') {
      useChatUiStore.getState().setTriage('all')
    }
  }, [canBroadcast])

  const goPrev = () => {
    if (conversations.length === 0) return
    const currentIndex =
      selectedConversationId == null
        ? -1
        : conversations.findIndex((conversation) => conversation.id === selectedConversationId)
    const startIndex = currentIndex === -1 ? conversations.length - 1 : currentIndex - 1

    for (let offset = 0; offset < conversations.length; offset += 1) {
      const index = (((startIndex - offset) % conversations.length) + conversations.length) % conversations.length
      const candidate = conversations[index]
      if (candidate.hasUnread && candidate.id !== selectedConversationId) {
        selectConversation(candidate.id)
        return
      }
    }
  }

  useChatShortcuts({
    onToggleDock: () => {
      if (!permitted || accessEnded) return
      toggleDock()
    },
    onNextUnread: () => {
      if (!permitted || accessEnded || !dockOpen) return
      goNext()
    },
    onPrevUnread: () => {
      if (!permitted || accessEnded || !dockOpen) return
      goPrev()
    },
    onEscape: () => {
      if (!permitted || accessEnded || !dockOpen) return
      if (broadcastPaneOpen) selectBroadcast(null)
      else if (selectedConversationId != null) selectConversation(null)
      else setDockOpen(false)
    },
  })

  if (!permitted || accessEnded || isChatRoute || !dockOpen) return null

  return (
    <div
      // Marks the dock for other global key handlers (see useSchedulerKeys.ts)
      // that need to ignore events from in here: this is a plain fixed div
      // with no role and no data-state, so it's invisible to the
      // role+data-state overlay selectors those handlers already check.
      data-chat-dock
      className={
        isMobile
          ? 'fixed inset-y-0 left-20 right-0 z-40 flex min-h-0 flex-col bg-surface'
          : 'fixed bottom-4 right-4 z-40 flex min-h-0 flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-2xl'
      }
      style={isMobile ? undefined : { width: size.width, height: size.height }}
    >
      {!isMobile && (
        <div
          role="separator"
          aria-label="Resize chat"
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
          className="absolute left-0 top-0 z-10 h-4 w-4 cursor-nwse-resize"
        />
      )}
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <p className="text-[13.5px] font-semibold text-ink">Chat</p>
        {unreadBadge > 0 && (
          <Badge tone="bad" className="h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px]">
            {unreadBadge > 99 ? '99+' : unreadBadge}
          </Badge>
        )}
        <div className="flex-1" />
        {isBroadcastMode ? (
          <Button variant="ghost" size="icon-sm" aria-label={CHAT_COPY.newBroadcast} onClick={composeBroadcast}>
            <SquarePen size={15} />
          </Button>
        ) : (
          <NewChatButton variant="icon" onOpened={selectConversation} />
        )}
        <StreamPill
          status={streamStatus}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: chatKeys.root })}
        />
        <button
          type="button"
          onClick={() => setDockOpen(false)}
          aria-label="Close chat"
          className="shrink-0 rounded-md p-1 text-ink-3 hover:bg-hover hover:text-ink"
        >
          <X size={15} />
        </button>
      </div>

      {selectedConversationId == null && !broadcastPaneOpen && (
        <div className="flex items-center gap-1.5 border-b border-line px-3 py-2">
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => setTriage(chip.value)}
              className={`rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors ${
                triage === chip.value ? 'bg-brand-accent-soft text-brand-accent-ink' : 'text-ink-3 hover:bg-hover'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col">
        {isBroadcastMode ? (
          broadcastPaneOpen ? (
            <BroadcastPane />
          ) : (
            <BroadcastList
              selectedId={selectedBroadcastId}
              onSelect={selectBroadcast}
              composeAction={
                <Button variant="outline" size="sm" onClick={composeBroadcast}>
                  {CHAT_COPY.newBroadcast}
                </Button>
              }
            />
          )
        ) : selectedConversationId == null ? (
          <ConversationList
            conversations={visibleConversations}
            selectedId={selectedConversationId}
            onSelect={(id) => selectConversation(id)}
            isLoading={conversationsQuery.isLoading}
            error={conversationsQuery.error}
            onRetry={() => void conversationsQuery.refetch()}
            triage={triage}
            emptyAction={<NewChatButton onOpened={selectConversation} />}
            kindHint={canOps ? 'ops' : 'chat'}
          />
        ) : (
          <>
            {nextUnreadId != null && (
              <div className="flex justify-end border-b border-line px-2 py-1.5">
                <Button variant="ghost" size="sm" onClick={goNext}>
                  Next unread
                </Button>
              </div>
            )}
            <div className="min-h-0 flex-1">
              <ChatThread
                key={selectedConversationId}
                conversationId={selectedConversationId}
                variant="dock"
                onBack={() => selectConversation(null)}
                onSendAndAdvance={goNext}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
